import { API_BASE } from './authFetch';

/**
 * Drag-and-drop card identification helper module.
 * Extracts card identity (Scryfall ID or card name) from HTML5 drag-and-drop data,
 * and provides async function to resolve full card data from Scryfall.
 *
 * @module dragDropCardParser
 */

// Custom drag data type set by in-app card tiles (CardView, SetView) for reliable Scryfall ID transfer.
export const SCRYFALL_ID_DRAG_TYPE = 'application/x-scryfall-id';

/**
 * Given a TCGPlayer product URL and a card name, extract the set slug from the URL.
 *
 * TCGPlayer product URLs follow the pattern:
 *   tcgplayer.com/product/{id}/magic-the-gathering-{set-slug}-{card-slug}
 *
 * Returns the set slug string (e.g. "commander-masters"), or null if it cannot be parsed.
 */
function extractSetSlugFromTcgUrl(url, cardName) {
  const match = url.match(/tcgplayer\.com\/product\/\d+\/magic(?:-the-gathering)?-([^?#\s]+)/i);
  if (!match) return null;

  const remainder = match[1]; // "{set-slug}-{card-slug}"

  // TCGPlayer lowercases names and replaces spaces with hyphens, but handles apostrophes
  // inconsistently across pages: some pages remove them ("teferis"), others expand them
  // to "-s-" ("teferi-s"). Try both to maximise match coverage.
  const base = cardName.toLowerCase().replace(/[^a-z0-9\s'-]/g, '').trim();

  const slugVariants = [
    base.replace(/'/g, '').replace(/\s+/g, '-'),   // apostrophe removed:  "teferis-protection"
    base.replace(/'/g, '-').replace(/\s+/g, '-').replace(/-+/g, '-'),  // apostrophe → hyphen: "teferi-s-protection"
  ];

  for (const cardSlug of slugVariants) {
    const suffix = `-${cardSlug}`;
    if (remainder.endsWith(suffix)) {
      return remainder.slice(0, -suffix.length);
    }
  }

  return null;
}

/**
 * Parses drag-and-drop dataTransfer to extract card identity information.
 *
 * Attempts to extract:
 * 1. Scryfall UUID from custom application/x-scryfall-id type (set by in-app card tiles)
 * 2. Scryfall UUID from URLs in text/uri-list or text/plain (cards.scryfall.io pattern)
 * 3. Card name + TCGPlayer product URL from text/html anchor/img
 * 4. Falls back to text/plain as raw card name
 *
 * @param {DataTransfer} dataTransfer - The drag event's dataTransfer object
 * @returns {Object|null} - { scryfallId, cardName, imageUrl, tcgPlayerUrl } or null if no card data found
 * @throws {Error} - Never throws; returns null on any parsing error
 */
export function parseDropData(dataTransfer) {
  try {
    const types = Array.from(dataTransfer.types || []);

    let scryfallId = null;
    let cardName = null;
    let imageUrl = null;
    let tcgPlayerUrl = null;

    // Highest-priority: custom type set by in-app card tiles (CardView, SetView)
    if (types.includes(SCRYFALL_ID_DRAG_TYPE)) {
      const id = dataTransfer.getData(SCRYFALL_ID_DRAG_TYPE);
      if (id && id.trim()) {
        scryfallId = id.trim();
      }
    }

    // Try to extract Scryfall UUID from URI list or plain text URLs (skipped if custom type already resolved ID)
    if (!scryfallId && (types.includes('text/uri-list') || types.includes('text/plain'))) {
      const textUri = dataTransfer.getData('text/uri-list') || dataTransfer.getData('text/plain');
      if (textUri) {
        // uri-list may be newline-separated; take the first non-comment entry
        const firstUri = textUri.split('\n').map(l => l.trim()).find(l => l && !l.startsWith('#')) || textUri.trim();

        // Match cards.scryfall.io image URLs — drop $ anchor to tolerate query params
        const uuidMatch = firstUri.match(/cards\.scryfall\.io\/.*\/([a-f0-9-]{36})\.[a-z]+/i);
        if (uuidMatch) {
          scryfallId = uuidMatch[1];
          imageUrl = firstUri;
        }

        // Match Scryfall card page URLs: scryfall.com/card/{set}/{num}/{name}
        if (!scryfallId) {
          const pageMatch = firstUri.match(/scryfall\.com\/card\/[^/]+\/[^/]+\/([^/?#]+)/i);
          if (pageMatch) {
            cardName = pageMatch[1].replace(/-/g, ' ');
          }
        }

        // Capture TCGPlayer product page URL when the user drags a link (not an image)
        if (!tcgPlayerUrl && /tcgplayer\.com\/product\/\d+/i.test(firstUri)) {
          tcgPlayerUrl = firstUri;
        }
      }
    }

    // Try to extract card name from HTML (img alt attribute) and TCGPlayer anchor href
    if (types.includes('text/html') && !scryfallId) {
      const html = dataTransfer.getData('text/html');
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const img = doc.querySelector('img');
        if (img) {
          const alt = img.getAttribute('alt');
          if (alt) {
            cardName = alt;
          }
          const src = img.getAttribute('src');
          if (src) {
            imageUrl = src;
            const imgUuidMatch = src.match(/cards\.scryfall\.io\/.*\/([a-f0-9-]{36})\.[a-z]+/i);
            if (imgUuidMatch) {
              scryfallId = imgUuidMatch[1];
            }
          }
        }

        // TCGPlayer product URL from the surrounding anchor — present when dragging an image
        // that is wrapped in a link on TCGPlayer search/set pages.
        if (!tcgPlayerUrl) {
          const anchor = doc.querySelector('a[href*="tcgplayer.com/product"]');
          if (anchor) {
            tcgPlayerUrl = anchor.getAttribute('href');
          }
        }
      }
    }

    // Fall back to text/plain as raw card name
    if (!cardName && !scryfallId && types.includes('text/plain')) {
      const plainText = dataTransfer.getData('text/plain');
      if (plainText && plainText.trim()) {
        cardName = plainText.trim();
      }
    }

    // Return null if we found nothing useful
    if (!scryfallId && !cardName) {
      return null;
    }

    return {
      scryfallId: scryfallId || null,
      cardName: cardName || null,
      imageUrl: imageUrl || null,
      tcgPlayerUrl: tcgPlayerUrl || null,
    };
  } catch (error) {
    console.error('[dragDropCardParser] Error parsing drop data:', error);
    return null;
  }
}

/**
 * Resolves the exact Scryfall card for a TCGPlayer product URL + card name.
 * Extracts the set slug from the URL, looks up the Scryfall set code, then
 * fetches the card with that specific set code.
 * Returns null (without throwing) if any step fails — caller falls back to name-only.
 */
async function resolveFromTcgPlayerUrl(tcgPlayerUrl, cardName) {
  try {
    const setSlug = extractSetSlugFromTcgUrl(tcgPlayerUrl, cardName);
    if (!setSlug) return null;

    const setName = setSlug.replace(/-/g, ' ');
    const setRes = await fetch(`${API_BASE}/sets/lookup?name=${encodeURIComponent(setName)}`);
    if (!setRes.ok) return null;

    const setData = await setRes.json();
    if (!setData?.code) return null;

    const cardRes = await fetch(
      `${API_BASE}/cards/named?exact=${encodeURIComponent(cardName)}&set=${encodeURIComponent(setData.code)}`
    );
    if (cardRes.ok) return await cardRes.json();

    return null;
  } catch {
    return null;
  }
}

/**
 * Resolves full card data from Scryfall based on parsed drop data.
 *
 * Strategy:
 * 1. If scryfallId is available, fetch from /cards/:id (fastest, most precise)
 * 2. If TCGPlayer product URL is present, extract the set and resolve the exact printing
 * 3. Fall back to /cards/named?exact= then /cards/named?fuzzy=
 *
 * @param {DataTransfer} dataTransfer - The drag event's dataTransfer object
 * @returns {Promise<Object|null>} - Full Scryfall card object or null if lookup fails
 */
export async function resolveCardFromDrop(dataTransfer) {
  try {
    const parsed = parseDropData(dataTransfer);
    if (!parsed) {
      return null;
    }

    // Fastest path: direct Scryfall ID (set by in-app card tiles)
    if (parsed.scryfallId) {
      const response = await fetch(`${API_BASE}/cards/${encodeURIComponent(parsed.scryfallId)}`);
      if (response.ok) {
        return await response.json();
      }
    }

    // TCGPlayer path: resolve the exact printing from the product URL
    if (parsed.cardName && parsed.tcgPlayerUrl) {
      const exactCard = await resolveFromTcgPlayerUrl(parsed.tcgPlayerUrl, parsed.cardName);
      if (exactCard) return exactCard;
      // Fall through to name-only if set resolution fails
    }

    // Name-only fallback (returns canonical/newest printing)
    if (parsed.cardName) {
      const response = await fetch(`${API_BASE}/cards/named?exact=${encodeURIComponent(parsed.cardName)}`);
      if (response.ok) {
        return await response.json();
      }

      const fuzzyResponse = await fetch(`${API_BASE}/cards/named?fuzzy=${encodeURIComponent(parsed.cardName)}`);
      if (fuzzyResponse.ok) {
        return await fuzzyResponse.json();
      }
    }

    return null;
  } catch (error) {
    console.error('[dragDropCardParser] Error resolving card from drop:', error);
    return null;
  }
}
